// Payrail KYC design tokens. UI chrome uses a readable sans-serif stack;
// monospace is reserved for code, identifiers and machine-readable values.

// ─── Light mode tokens (default) ───
const light = {
  paper:      '#f5f7fb',
  ink:        '#172033',
  mid:        '#5b687a',
  soft:       '#8591a3',
  rule:       '#e2e7ef',
  ruleStrong: '#cfd7e3',
  panel:      '#ffffff',
  accent:     '#516fe8',
  accentInk:  '#3854c7',
  accentSoft: 'rgba(81,111,232,0.10)',
  flag:       'oklch(0.68 0.17 28)',
  flagSoft:   'oklch(0.95 0.04 28)',
} as const;

// ─── Dark mode tokens ───
const dark = {
  paper:      '#111720',
  ink:        '#f2f5f9',
  mid:        '#b2bdca',
  soft:       '#7f8b9a',
  rule:       '#293342',
  ruleStrong: '#3a4657',
  panel:      '#18212c',
  accent:     '#8ba4ff',
  accentInk:  '#aebeff',
  accentSoft: 'rgba(139,164,255,0.13)',
  flag:       'oklch(0.76 0.18 28)',
  flagSoft:   'oklch(0.24 0.06 28)',
} as const;

// ─── Shared (mode-independent) tokens ───
export const C = {
  // CSS variables keep inline style objects in sync with the active theme.
  bg:           'var(--paper)',
  panel:        'var(--panel)',
  sidebar:      'var(--panel)',
  surface:      'var(--panel-2)',
  surfaceHover: 'var(--panel-3)',
  border:       'var(--rule)',
  borderStrong: 'var(--rule-strong)',

  // Accent (green)
  cyan:         'var(--accent)',
  cyanDim:      'var(--accent-soft)',
  cyanBorder:   'color-mix(in oklab, var(--accent) 32%, transparent)',
  accent:       'var(--accent)',
  accentInk:    'var(--accent-ink)',
  accentSoft:   'var(--accent-soft)',

  // Semantic status colors
  green:        '#34d399',
  greenDim:     'rgba(52,211,153,0.1)',
  red:          '#f87171',
  redDim:       'rgba(248,113,113,0.1)',
  blue:         '#60a5fa',
  blueDim:      'rgba(96,165,250,0.12)',
  amber:        '#fbbf24',
  amberDim:     'rgba(251,191,36,0.1)',
  orange:       '#fb923c',
  orangeDim:    'rgba(251,146,60,0.1)',
  purple:       '#a78bfa',
  purpleDim:    'rgba(167,139,250,0.1)',

  // Typography
  text:         'var(--ink)',
  muted:        'var(--mid)',
  dim:          'var(--soft)',
  code:         '#86efac',
  codeBg:       'var(--paper-2)',

  // Font stacks
  mono:         '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  sans:         'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

  // Light/dark mode sets (for components that need explicit mode tokens)
  light,
  dark,
} as const;

export type ColorTokens = typeof C;

/** Inject the single UI font once per page. Code uses the local system mono. */
export function injectFonts() {
  const id = 'payrail-kyc-fonts';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

/** Get the current theme from <html> data-theme attribute */
export function getTheme(): 'light' | 'dark' {
  return (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light';
}

/** Toggle theme between light and dark */
export function toggleTheme(): 'light' | 'dark' {
  const current = getTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  localStorage.setItem('payrail-kyc-theme', next);
  return next;
}

/** Initialize theme from localStorage or default to light. */
export function initTheme() {
  const stored = localStorage.getItem('payrail-kyc-theme') as 'light' | 'dark' | null;
  document.documentElement.setAttribute('data-theme', stored || 'light');
}
